import type { LienTypeOption, LienStatusOption, QboPushErrorDetail } from '@/models/lienTypes';

// ── Lien Type Options (for dropdowns) ────────────────────────────────────

export const LIEN_TYPE_OPTIONS: LienTypeOption[] = [
  { id: 'medicalProvider', label: 'Medical Provider' },
  { id: 'insuranceSubrogation', label: 'Insurance Subrogation' },
  { id: 'governmentMedicare', label: 'Government / Medicare' },
  { id: 'letterOfProtection', label: 'Letter of Protection' },
  { id: 'attorneyLien', label: 'Attorney Lien' },
  { id: 'other', label: 'Other' },
];

// ── Lien Status Options (for dropdowns) ──────────────────────────────────

// `pushedToQbo` is intentionally excluded — it's an automated terminal state
// set only by a successful QBO push, and the backend rejects selecting it
// manually. It must never appear as a selectable dropdown option.
export const LIEN_STATUS_OPTIONS: LienStatusOption[] = [
  { value: 'pending', label: 'Pending', color: 'grey' },
  { value: 'negotiating', label: 'Negotiating', color: 'blue' },
  { value: 'finalized', label: 'Finalized', color: 'green' },
];

export const STATUS_DOT_COLORS: Record<string, string> = {
  pending: '#94a3b8',
  negotiating: '#3b82f6',
  finalized: '#22c55e',
};

export const STATUS_STYLES: Record<string, { background: string; color: string; border: string }> = {
  pending:     { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' },
  negotiating: { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
  finalized:   { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' },
};

/**
 * Chip label for every lien status, including `pushedToQbo` — which is
 * excluded from `LIEN_STATUS_OPTIONS` since it can't be manually selected but
 * still needs to render correctly wherever a lien's current status is shown.
 * Keyed lowercase to match the case-insensitive convention used throughout
 * the lien UI (see lienHelpers' `isFinalized`/`isPushed`).
 */
export const LIEN_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  negotiating: 'Negotiating',
  finalized: 'Finalized',
  pushedtoqbo: 'Submitted to QuickBooks',
};

/**
 * User-facing copy + remediation steps for known QBO push error keys.
 * The backend usually sends a human-readable message string; when it sends a
 * recognised key we enrich it with guided resolution steps (matches the
 * "Push Error Details" modal in the V2 design).
 */
export const QBO_PUSH_ERRORS: Record<string, QboPushErrorDetail> = {
  VENDOR_NOT_IN_QBO: {
    message:
      'The lienholder does not exist as a vendor in QuickBooks.',
    steps: [
      'Sync vendors from QuickBooks (Settings → Integrations → QBO Sync).',
      'Or create the vendor manually in QuickBooks.',
      'Once synced, retry the push.',
    ],
  },
  NO_TRUST_ACCOUNT: {
    message: 'No trust account is configured for this matter in QuickBooks.',
    steps: [
      'Open Settings → Trust Accounting and configure a trust bank + liability account.',
      'Ensure the matter is connected to QuickBooks.',
      'Retry the push.',
    ],
  },
  INSUFFICIENT_TRUST_BALANCE: {
    message: 'The trust account balance is insufficient to cover this lien amount.',
    steps: [
      'Review the trust balance for this client in QuickBooks.',
      'Ensure sufficient funds are available before pushing.',
      'Contact accounting if a trust deposit is needed.',
    ],
  },
  RECONCILED_CHECK: {
    message:
      'The QuickBooks trust check has already been reconciled and can no longer be undone.',
    steps: [
      'Reconciled checks are locked in QuickBooks.',
      'If this was a mistake, un-reconcile the check in QuickBooks first.',
      'Then retry the undo.',
    ],
  },
  QBO_TIMEOUT: {
    message: 'QuickBooks did not respond in time. This is usually transient.',
    steps: [
      'Wait a few minutes and retry — QuickBooks requests occasionally time out.',
      'Check the QuickBooks status page (status.intuit.com) for outages.',
      'If it persists, contact LeanLaw support.',
    ],
  },
};

/** Back-compat: message-only map derived from QBO_PUSH_ERRORS. */
export const QBO_PUSH_ERROR_MESSAGES: Record<string, string> = Object.keys(
  QBO_PUSH_ERRORS,
).reduce<Record<string, string>>((acc, key) => {
  acc[key] = QBO_PUSH_ERRORS[key].message;
  return acc;
}, {});

/** Generic fallback resolution steps when the error key isn't recognised. */
export const QBO_PUSH_GENERIC_STEPS: string[] = [
  'Verify QuickBooks is connected (Settings → Integrations).',
  'Confirm the matter and lienholder vendor are synced to QuickBooks.',
  'Wait a moment and retry the push.',
];
